import { Button, CloseButton, Dialog, HStack, Text, VStack } from '@chakra-ui/react'
import { ResponsiveModal } from '../shared/ResponsiveModal'
import { StatusChip } from '../shared/StatusChip'
import { BillContentDetails } from './BillContentDetails'
import { LegiScanCredit } from './LegiScanCredit'
import { billStatusVariant } from './billStatus'
import type { AskBill } from '../../types/lobbyDay'

interface LinkedBillDetailSheetProps {
  /** The bill to show, or null to keep the sheet closed. */
  bill: AskBill | null
  onClose: () => void
}

/**
 * B1: read-only detail view of a bill linked to an ask — the organizer's counterpart to what the
 * attendee sees in the packet. All data is already on `AskBill` (summary/fullTextUrl/status), so
 * this is purely presentational; it reuses the packet's `BillContentDetails` "Official summary"
 * block + `LegiScanCredit` so both sides render the same shape. Full-screen on mobile per
 * ResponsiveModal.
 */
export function LinkedBillDetailSheet({ bill, onClose }: LinkedBillDetailSheetProps) {
  const hasContent = !!(bill?.summary || bill?.fullTextUrl)
  return (
    <ResponsiveModal isOpen={bill !== null} onClose={onClose} size="md" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Bill details</Dialog.Title>
          </Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" onClick={onClose} /></Dialog.CloseTrigger>
          {bill && (
            <Dialog.Body>
              <VStack align="stretch" gap={3}>
                <VStack align="stretch" gap={1}>
                  <HStack gap={2} align="baseline" wrap="wrap">
                    <Text fontWeight="bold">{bill.billNumber}</Text>
                    {bill.title && <Text fontSize="sm" color="muted">· {bill.title}</Text>}
                  </HStack>
                  <HStack gap={2} wrap="wrap">
                    <StatusChip variant={billStatusVariant(bill.statusLabel)}>
                      {bill.statusLabel ?? 'Status unknown'}
                    </StatusChip>
                    {bill.nextEventLabel && (
                      <Text fontSize="xs" color="muted">
                        Next: {bill.nextEventLabel}{bill.nextEventDate ? ` · ${bill.nextEventDate}` : ''}
                      </Text>
                    )}
                  </HStack>
                </VStack>

                <VStack align="stretch" gap={1}>
                  <Text fontWeight="semibold" fontSize="sm">Official summary</Text>
                  {hasContent
                    ? <BillContentDetails summary={bill.summary} fullTextUrl={bill.fullTextUrl} />
                    : <Text fontSize="sm" color="muted">No summary available for this bill yet.</Text>}
                </VStack>

                <LegiScanCredit />
              </VStack>
            </Dialog.Body>
          )}
          <Dialog.Footer>
            <Button colorPalette="brand" minH="44px" onClick={onClose}>Close</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
